import { FC, useContext, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import MapContext from "../../contexts/data/MapContext";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import SpeciesContentPage from "../SpeciesContentPage";

interface CustomHomePageRedirectProps {
    route: string;
}

const CustomHomePageRedirect: FC<CustomHomePageRedirectProps> = ({
    route
}) => {
    const params = useParams();
    const { setLayersFilter } = useContext(MapContext);
    const { species: { key } } = useContext(SpeciesContext);
    const { setMapCenter } = useContext(MapContext);

    const isWildDogScan = params.species === "wilddogscan";
    const isDeerScan = params.species === "deerscan";
    const isFeralPigScan = params.species === "feralpigscan";
    const isFoxScan = params.species === "foxscan";
    const isFeralCatScan = params.species === "feralcatscan";
    const isFeralGoatScan = params.species === "feralgoatscan";
    const isMynaScan = params.species === "mynascan";
    const isRabbitScan = params.species === "rabbitscan";
    const isToadScan = params.species === "toadscan";
    const isFeralFishScan = params.species === "feralfishscan";

    // -- NSW Route --
    useEffect(() => {
        if (route !== "nsw" || (!isWildDogScan && !isDeerScan && !isFeralPigScan && !isFoxScan)) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["All"],
            customHomePageRegions: [
                { title: "All", fileName: "NSW_LLS_regions_simplified_colourTomato.kmz" },
                { title: "Central Tablelands LLS", fileName: "central_tablelands_lls.zip" },
                { title: "Central West LLS", fileName: "central_west_lls.zip" },
                { title: "Greater Sydney LLS", fileName: "greater_sydney_lls.zip" },
                { title: "Hunter LLS", fileName: "hunter_lls.zip" },
                { title: "Murray LLS", fileName: "murray_lls.zip" },
                { title: "North Coast LLS", fileName: "north_coast_lls.zip" },
                { title: "Northern Tablelands LLS", fileName: "northern_tablelands_lls.zip" },
                { title: "Northwest LLS", fileName: "northwest_lls.zip" },
                { title: "Riverina LLS", fileName: "riverina_lls.zip" },
                { title: "South East LLS", fileName: "south_east_lls.zip" },
                { title: "Western LLS", fileName: "western_lls.zip" }
            ]
        }));
    }, [route, isWildDogScan, isDeerScan, isFeralPigScan, isFoxScan, setLayersFilter]);

    // -- LLS Route --
    useEffect(() => {
        if (route !== "lls" || (!isFeralCatScan && !isFeralGoatScan && !isFeralPigScan && !isFoxScan && !isMynaScan && !isRabbitScan && !isToadScan && !isWildDogScan)) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["All"],
            customHomePageRegions: [
                { title: "All", fileName: "NSW_LLS_regions_simplified_colourTomato.kmz" },
                { title: "Central Tablelands LLS", fileName: "central_tablelands_lls.zip" },
                { title: "Central West LLS", fileName: "central_west_lls.zip" },
                { title: "Greater Sydney LLS", fileName: "greater_sydney_lls.zip" },
                { title: "Hunter LLS", fileName: "hunter_lls.zip" },
                { title: "Murray LLS", fileName: "murray_lls.zip" },
                { title: "North Coast LLS", fileName: "north_coast_lls.zip" },
                { title: "Northern Tablelands LLS", fileName: "northern_tablelands_lls.zip" },
                { title: "Northwest LLS", fileName: "northwest_lls.zip" },
                { title: "Riverina LLS", fileName: "riverina_lls.zip" },
                { title: "South East LLS", fileName: "south_east_lls.zip" },
                { title: "Western LLS", fileName: "western_lls.zip" }
            ]
        }));
    }, [route, isFeralCatScan, isFeralGoatScan, isFeralPigScan, isFoxScan, isMynaScan, isRabbitScan, isToadScan, isWildDogScan, setLayersFilter]);

    // -- King Island Route --
    useEffect(() => {
        if (route !== "kingisland" || !isDeerScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));
    }, [route, isDeerScan, setLayersFilter]);

    // -- Northern Rivers Route --
    useEffect(() => {
        if (route !== "northernrivers" || !isDeerScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Project Area"],
            customHomePageRegions: [
                { title: "Project Area", fileName: "NorthernRivers_ProjectArea.zip" }
            ]
        }));
    }, [route, isDeerScan, setLayersFilter]);

    // -- PHBG Route --
    useEffect(() => {
        if (route !== "phbg" || (!isDeerScan && !isFoxScan)) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Project Area"],
            customHomePageRegions: [
                { title: "Project Area", fileName: "PHBG_ProjectArea.zip" }
            ]
        }));
    }, [route, isDeerScan, isFoxScan, setLayersFilter]);

    // -- Kangaroo Island Route --
    useEffect(() => {
        if (route !== "kangarooisland" || !isFeralCatScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));

        setMapCenter({latitude: -35.77, longitude: 137.35, zoom: 10});
    }, [route, isFeralCatScan, setLayersFilter, setMapCenter]);

    // -- Bruny Island Route --
    useEffect(() => {
        if (route !== "brunyisland" || !isFeralCatScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Bruny Island"],
            customHomePageRegions: [
                {title: "Bruny Island", fileName: "Bruny.kmz"}
            ]
        }));
    }, [route, isFeralCatScan, setLayersFilter]);

    // -- Upper Murrumbidgee Route --
    useEffect(() => {
        if (route !== "uppermurrumbidgee" || !isFeralFishScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));
    }, [route, isFeralFishScan, setLayersFilter]);

    // -- Sydney South Route --
    useEffect(() => {
        if (route !== "sydneysouth" || !isFoxScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Project Area"],
            customHomePageRegions: [
                { title: "Project Area", fileName: "Sydney_South_-_South_Sydney.zip" },
                { title: "Ashfield", fileName: "Sydney_South_-_Ashfield.zip" },
                { title: "Auburn", fileName: "Sydney_South_-_Auburn.zip" },
                { title: "Bankstown", fileName: "Sydney_South_-_Bankstown.zip" },
                { title: "Canada Bay", fileName: "Sydney_South_-_Canada_Bay.zip" },
                { title: "Canterbury", fileName: "Sydney_South_-_Canterbury.zip" },
                { title: "City of Sydney", fileName: "Sydney_South_-_City_Of_Sydney.zip" },
                { title: "Hurstville", fileName: "Sydney_South_-_Hurtsville.zip" },
                { title: "Kogarah", fileName: "Sydney_South_-_Kogarah.zip" },
                { title: "Leichhardt", fileName: "Sydney_South_-_Leichhardt.zip" },
                { title: "Marrickville", fileName: "Sydney_South_-_Marrickville.zip" },
                { title: "Randwick", fileName: "Sydney_South_-_Randwick.zip" },
                { title: "Rockdale", fileName: "Sydney_South_-_Rockdale.zip" },
                { title: "Strathfield", fileName: "Sydney_South_-_Strathfield.zip" },
                { title: "Sutherland", fileName: "Sydney_South_-_Sutherland.zip" },
                { title: "Waverley", fileName: "Sydney_South_-_Waverley.zip" },
            ]
        }));
    }, [route, isFoxScan, setLayersFilter]);

    // -- Sydney North Route --
    useEffect(() => {
        if (route !== "sydneynorth" || !isFoxScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Project Area"],
            customHomePageRegions: [
                { title: "Project Area", fileName: "Sydney_North_-_ProjectArea.zip" },
                { title: "Auburn", fileName: "Sydney_North_-_Auburn.zip" },
                { title: "Hawkesbury", fileName: "Sydney_North_-_Hawkesbury.zip" },
                { title: "Hornsby", fileName: "Sydney_North_-_Hornsby.zip" },
                { title: "Hunters Hill", fileName: "Sydney_North_-_HuntersHill.zip" },
                { title: "Ku-ring-gai", fileName: "Sydney_North_-_KuRingGai.zip" },
                { title: "Lane Cove", fileName: "Sydney_North_-_LaneCove.zip" },
                { title: "Manly", fileName: "Sydney_North_-_Manly.zip" },
                { title: "Mosman", fileName: "Sydney_North_-_Mosman.zip" },
                { title: "North Sydney", fileName: "Sydney_North_-_NorthSydney.zip" },
                { title: "Parramatta", fileName: "Sydney_North_-_Parramatta.zip" },
                { title: "Pittwater", fileName: "Sydney_North_-_Pittwater.zip" },
                { title: "Ryde City", fileName: "Sydney_North_-_RydeCity.zip" },
                { title: "The Hills", fileName: "Sydney_North_-_TheHills.zip" },
                { title: "Warringah", fileName: "Sydney_North_-_Warringah.zip" },
                { title: "Willoughby City", fileName: "Sydney_North_-_WilloughbyCity.zip" },
            ]
        }));
    }, [route, isFoxScan, setLayersFilter]);

    // -- Menangle Fox Route --
    useEffect(() => {
        if (route !== "menanglefox" || !isFoxScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));

        setMapCenter({latitude: -34.15, longitude: 150.72, zoom: 13});
    }, [route, isFoxScan, setLayersFilter, setMapCenter]);

    // -- CIMAG Route --
    useEffect(() => {
        if (route !== "cimag" || !isMynaScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));

        setMapCenter({latitude: -35.50, longitude: 149.01, zoom: 10});
    }, [route, isMynaScan, setLayersFilter, setMapCenter]);

    // -- Mallee Route --
    useEffect(() => {
        if (route !== "mallee" || !isRabbitScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: ["Mallee"],
            customHomePageRegions: [
                { title: "Mallee", fileName: "rabbit_mallee_cma_export_2015.zip" },
                { title: "Swan Hill", fileName: "rabbit_swanhill_lga.zip" }
            ]
        }));
    }, [route, isRabbitScan, setLayersFilter]);

    // -- Phillip Island Route --
    useEffect(() => {
        if (route !== "phillipisland" || !isRabbitScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));

        setMapCenter({latitude: -38.49, longitude: 145.25, zoom: 13});
    }, [route, isRabbitScan, setLayersFilter, setMapCenter]);

    // -- North Coast Route --
    useEffect(() => {
        if (route !== "northcoast" || !isWildDogScan) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: undefined,
            customHomePageRegions: undefined
        }));
    }, [route, isRabbitScan, setLayersFilter]);

    // -- NSW Route --
    if (route === "nsw") {
        if (isWildDogScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/wilddog_homepage_nsw`}
                />
            );
        }
    
        if (isDeerScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/deer_homepage_nsw`}
                />
            );
        }
    
        if (isFeralPigScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralpig_homepage_nsw`}
                />
            );
        }
    
        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_nsw`}
                />
            );
        }
    
        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- LLS Route --
    if (route === "lls") {
        if (isFeralCatScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralcat_homepage_lls`}
                />
            );
        }

        if (isFeralGoatScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralgoat_homepage_lls`}
                />
            );
        }

        if (isFeralPigScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralpig_homepage_lls`}
                />
            );
        }

        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_lls`}
                />
            );
        }

        if (isMynaScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/myna_homepage_lls`}
                />
            );
        }

        if (isRabbitScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/rabbit_homepage_lls`}
                />
            );
        }

        if (isToadScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/toad_homepage_lls`}
                />
            );
        }

        if (isWildDogScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/nsw`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- King Island Route --
    if (route === "kingisland") {
        if (isDeerScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/deer_homepage_kingisland`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Northern Rivers Route --
    if (route === "northernrivers") {
        if (isDeerScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/deer_homepage_northernrivers`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- PHBG Route --
    if (route === "phbg") {
        if (isDeerScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/deer_homepage_phbg`}
                />
            );
        }

        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_phbg`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Kangaroo Island Route --
    if (route === "kangarooisland") {
        if (isFeralCatScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralcat_homepage_kangarooisland`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Bruny Island Route --
    if (route === "brunyisland") {
        if (isFeralCatScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralcat_homepage_brunyisland`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Upper Murrumbidgee Route --
    if (route === "uppermurrumbidgee") {
        if (isFeralFishScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/feralfish_homepage_uppermurrumbidgee`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Sydney South Route --
    if (route === "sydneysouth") {
        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_sydneysouth`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Sydney North Route --
    if (route === "sydneynorth") {
        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_sydneynorth`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Menangle Fox Route --
    if (route === "menanglefox") {
        if (isFoxScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/fox_homepage_menanglefox`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- CIMAG Route --
    if (route === "cimag") {
        if (isMynaScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/myna_homepage_CIMAG`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Mallee Route --
    if (route === "mallee") {
        if (isRabbitScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/rabbit_homepage_mallee`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- Phillip Island Route --
    if (route === "phillipisland") {
        if (isRabbitScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/rabbit_homepage_phillipisland`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    // -- North Coast Route --
    if (route === "northcoast") {
        if (isWildDogScan) {
            return (
                <Navigate
                    replace
                    to={`/${params.species}/content/wilddog_homepage_northcoast`}
                />
            );
        }

        if (params.species) {
            return (
                <SpeciesContentPage page={`${key}_homepage`} />
            );
        }
    }

    return <Navigate replace to="/error/404" />;
};

export default CustomHomePageRedirect;
